import { ColDef } from "ag-grid-community";
import moment from "moment";

/**
 * A row of `api/admin/get-user-list`. The endpoint runs
 * `select * from du_config.du_ana_user_master` and returns the DataTable as-is,
 * so the column set can change without a frontend release — hence the index
 * signature. The named members are display-only values derived from the raw
 * columns (see `buildUserRow`) and are what the popups / renderers bind to.
 */
export interface DuAdminUserRow {
  [column: string]: any;
  name: string;
  email: string;
  userType: string;
  status: "Active" | "Inactive";
  /** Not in the user master yet — the row menu drives these, so they stay null
   *  until the auth-type endpoints exist. */
  authType: "Email" | "Authenticator" | "Okta" | null;
  lastInviteSent: string | null;
}

/**
 * The only columns the directory shows, in display order. `get-user-list` runs
 * `select *`, so this is an allow-list: anything else in the payload — including
 * `password` — is never rendered and never reaches the Excel export.
 *
 * Matched case-insensitively, and the payload's own casing is kept for the
 * `field`, so an upper-case DataTable still binds.
 */
const USER_GRID_COLUMNS = [
  "user_id",
  "usr_display_nm",
  "usr_fst_nm",
  "usr_mid_nm",
  "usr_lst_nm",
  "gender",
  "usr_email_id",
  "employee_key",
  "usr_actvtn_strt_dt",
  "usr_actvtn_end_dt",
  "impersonation_flg",
  "user_type",
  "alert_setting_flg",
  "multi_schema_flg",
];

const USER_COLUMN_HEADERS: { [column: string]: string } = {
  user_id: "USER ID",
  usr_display_nm: "DISPLAY NAME",
  usr_fst_nm: "FIRST NAME",
  usr_mid_nm: "MIDDLE NAME",
  usr_lst_nm: "LAST NAME",
  gender: "GENDER",
  usr_email_id: "EMAIL ADDRESS",
  employee_key: "EMPLOYEE KEY",
  usr_actvtn_strt_dt: "ACTIVATION DATE",
  usr_actvtn_end_dt: "EXPIRY DATE",
  impersonation_flg: "IMPERSONATION",
  user_type: "USER TYPE",
  alert_setting_flg: "ALERT SETTING",
  multi_schema_flg: "MULTI SCHEMA",
};

/** Code columns shown as their label. Applied through a value getter rather than
 *  a formatter so sorting, filtering and the quick search all work on the label
 *  the user can actually see. */
const CODE_LABELS: { [column: string]: { [code: string]: string } } = {
  gender: { M: "Male", F: "Female", O: "Other" },
  user_type: { I: "Internal", E: "External" },
};

/** Expansions for the abbreviations used across the DU schema, applied when a
 *  column is not in `USER_COLUMN_HEADERS`. */
const HEADER_TOKENS: { [token: string]: string } = {
  usr: "USER",
  nm: "NAME",
  dt: "DATE",
  ts: "TIMESTAMP",
  flg: "FLAG",
  ind: "INDICATOR",
  fst: "FIRST",
  mid: "MIDDLE",
  lst: "LAST",
  actvtn: "ACTIVATION",
  strt: "START",
  crtd: "CREATED",
  updt: "UPDATED",
  mod: "MODIFIED",
  desc: "DESCRIPTION",
  cnt: "COUNT",
  addr: "ADDRESS",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;

function escapeHtml(value: any): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Case-insensitive column read — DataTable casing depends on the driver. */
function pick(raw: any, column: string): any {
  if (!raw) return null;
  if (raw[column] !== undefined) return raw[column];
  const key = Object.keys(raw).find(
    (k) => k.toLowerCase() === column.toLowerCase()
  );
  return key ? raw[key] : null;
}

function isBlank(value: any): boolean {
  return value === null || value === undefined || String(value).trim() === "";
}

function isDateColumn(column: string, sample: any): boolean {
  if (/(_dt|_date|date|_ts)$/i.test(column)) return true;
  return typeof sample === "string" && ISO_DATE.test(sample);
}

function isFlagColumn(column: string): boolean {
  return /(_flg|_flag)$/i.test(column);
}

function isTruthyFlag(value: any): boolean {
  return /^(y|yes|1|t|true|a|active)$/i.test(String(value).trim());
}

/** Dates come back as `2025-02-09T00:00:00`; drop the time when it is midnight
 *  so date-only columns do not read as "12:00 AM". */
function formatDateValue(value: any): string {
  const parsed = moment(value, moment.ISO_8601, false);
  if (!parsed.isValid()) return String(value);
  const hasTime =
    parsed.hours() > 0 || parsed.minutes() > 0 || parsed.seconds() > 0;
  return hasTime
    ? parsed.format("MM/DD/YYYY hh:mm A")
    : parsed.format("MM/DD/YYYY");
}

function humanizeColumn(column: string): string {
  if (USER_COLUMN_HEADERS[column.toLowerCase()]) {
    return USER_COLUMN_HEADERS[column.toLowerCase()];
  }
  return column
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .split(/[_\s]+/)
    .filter((token) => !!token)
    .map((token) => HEADER_TOKENS[token.toLowerCase()] || token.toUpperCase())
    .join(" ");
}

/** Keeps every raw column and adds the values the popups and stat cards bind
 *  to. These are deliberately *not* grid columns — the grid shows DB columns
 *  only. */
export function buildUserRow(raw: any): DuAdminUserRow {
  const displayName = pick(raw, "usr_display_nm");
  const first = pick(raw, "usr_fst_nm");
  const last = pick(raw, "usr_lst_nm");
  const userId = pick(raw, "user_id");

  const name = !isBlank(displayName)
    ? String(displayName).trim()
    : [first, last].filter((part) => !isBlank(part)).join(" ").trim() ||
      String(userId || "");

  return {
    ...raw,
    name,
    email: String(pick(raw, "usr_email_id") || userId || ""),
    // Read straight off the row's own `user_type` code — no lookup call.
    userType: userTypeLabel(pick(raw, "user_type")),
    status: resolveStatus(raw),
    authType: null,
    lastInviteSent: null,
  };
}

/** One entry of the User Type dropdown: the code as stored in `user_type`, and
 *  the label shown for it. The list is fixed on the client — Internal (`I`) and
 *  External (`E`) — so nothing reads it off a lookup response. */
export interface DuUserTypeOption {
  value: string;
  label: string;
}

/** `I`/`E` → Internal/External, matching the grid column. Blank stays blank so
 *  the KPI count does not treat "unset" as a type. */
export function userTypeLabel(value: any): string {
  if (isBlank(value)) return "";
  const code = String(value).trim().toUpperCase();
  return CODE_LABELS["user_type"][code] || String(value).trim();
}

/**
 * What `check-user-exists` answers with:
 *
 *     { "user_exist": true, "user_linked_to_client": false,
 *       "message": "Client configuration is not done for this client" }
 *
 * Two INDEPENDENT flags, and the whole point of the endpoint is that they are
 * not the same question. The id is looked up in the user master, and separately
 * against the `client_key` that was sent:
 *
 *   - `user_exist` alone — a registered user of some other client, whom this
 *     client can be added to. Not a refusal.
 *   - both — already configured here. The one case the create screen refuses.
 *   - neither — a new id.
 *
 * Sent without a `client_key` — or with Global (9999), which owns no users —
 * nothing can be linked, so `user_linked_to_client` is false and `message`
 * falls back to plain existence wording.
 */
export interface DuUserExistsCheck {
  /** `user_exist`: the id is in the user master, under ANY client. */
  userExist: boolean;
  /** `user_linked_to_client`: that user is already configured for the
   *  `client_key` that was sent. */
  linkedToClient: boolean;
  /** The API's own wording for `user_linked_to_client`, ready to display. */
  message: string;
}

function isTrue(value: any): boolean {
  return /^(true|1|y|yes)$/i.test(String(value).trim());
}

/**
 * Reads the check into the three fields above.
 *
 * The existence flag is spelled `user_exist` — singular — and the two other
 * spellings are accepted for it because the endpoint has carried each of them in
 * turn: `user_exists`, and the bare `exists` of the build from before it took a
 * `client_key`. First one present wins, so a `false` is read as false rather
 * than falling through to the next spelling, and a bare boolean or string is
 * tolerated for the same reason.
 *
 * `user_linked_to_client` is absent on those older builds, where the single flag
 * meant both things and the screen blocked on it. It falls back to `user_exist`
 * rather than to false, so an old API still refuses what it used to refuse
 * instead of silently letting every duplicate through.
 */
export function readUserExistsCheck(res: any): DuUserExistsCheck {
  const body =
    res !== null && typeof res === "object" ? res : { user_exist: res };

  const userExist = isTrue(
    pick(body, "user_exist") ?? pick(body, "user_exists") ?? pick(body, "exists")
  );
  const linked = pick(body, "user_linked_to_client");

  return {
    userExist,
    linkedToClient: linked == null ? userExist : isTrue(linked),
    message: String(pick(body, "message") ?? "").trim(),
  };
}

/** Prefers `usr_act_flg`; falls back to whichever end date the table carries so
 *  the status column is still meaningful if the flag is absent. */
function resolveStatus(raw: any): "Active" | "Inactive" {
  const flag = pick(raw, "usr_act_flg");
  if (!isBlank(flag)) return isTruthyFlag(flag) ? "Active" : "Inactive";

  const end = pick(raw, "end_date") ?? pick(raw, "usr_actvtn_end_dt");
  if (isBlank(end)) return "Active";
  const parsed = moment(end, moment.ISO_8601, false);
  if (!parsed.isValid()) return "Active";
  return parsed.isSameOrAfter(moment(), "day") ? "Active" : "Inactive";
}

/**
 * Builds the columns listed in `USER_GRID_COLUMNS`, in that order. Only the row
 * actions are pinned — every data column, `user_id` included, scrolls with the
 * grid.
 *
 * Every listed column is shown whether or not it holds data — the list is
 * explicit, so a blank column is a fact about the client rather than noise to
 * hide.
 */
export function buildUserColumnDefs(
  rawRows: any[],
  actionsRenderer: any
): ColDef[] {
  const fields = collectFields(rawRows);
  const samples = collectSamples(rawRows, fields);

  const columns: ColDef[] = fields.map((field) => {
    const def = buildDataColumnDef(field, samples[field]);
    // Long enough for an email address, which is what `user_id` holds.
    return field.toLowerCase() === "user_id" ? { ...def, minWidth: 200 } : def;
  });

  columns.push({
    headerName: "",
    field: "actions",
    minWidth: 60,
    maxWidth: 60,
    pinned: "right",
    sortable: false,
    filter: false,
    resizable: false,
    suppressMenu: true,
    cellRenderer: actionsRenderer,
  });

  return columns;
}

/** Walks `USER_GRID_COLUMNS` in order and keeps the ones the payload actually
 *  carries, using the payload's own key casing. */
function collectFields(rawRows: any[]): string[] {
  const available: string[] = [];
  (rawRows || []).forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!available.some((k) => k.toLowerCase() === key.toLowerCase())) {
        available.push(key);
      }
    });
  });

  return USER_GRID_COLUMNS.map((wanted) =>
    available.find((key) => key.toLowerCase() === wanted)
  ).filter((key): key is string => !!key);
}

/** First non-blank value per column, used to detect date columns. */
function collectSamples(rawRows: any[], fields: string[]): { [f: string]: any } {
  const samples: { [field: string]: any } = {};
  fields.forEach((field) => {
    const row = (rawRows || []).find((r) => !isBlank(r?.[field]));
    samples[field] = row ? row[field] : null;
  });
  return samples;
}

function buildDataColumnDef(field: string, sample: any): ColDef {
  const base: ColDef = {
    headerName: humanizeColumn(field),
    field,
    minWidth: 140,
  };

  const labels = CODE_LABELS[field.toLowerCase()];
  if (labels) {
    return {
      ...base,
      minWidth: 130,
      valueGetter: (params: any) => {
        const raw = params.data?.[field];
        if (isBlank(raw)) return "";
        const code = String(raw).trim().toUpperCase();
        return labels[code] || String(raw);
      },
      cellRenderer: (params: any) =>
        isBlank(params.value) ? emptyCell() : escapeHtml(params.value),
    };
  }

  if (isFlagColumn(field)) {
    return {
      ...base,
      minWidth: 120,
      valueFormatter: (params: any) =>
        isBlank(params.value) ? "" : isTruthyFlag(params.value) ? "Yes" : "No",
      cellRenderer: (params: any) =>
        isBlank(params.value)
          ? emptyCell()
          : `<span class="flag-badge ${
              isTruthyFlag(params.value) ? "yes" : "no"
            }">${isTruthyFlag(params.value) ? "Yes" : "No"}</span>`,
    };
  }

  if (isDateColumn(field, sample)) {
    return {
      ...base,
      minWidth: 160,
      filter: "agDateColumnFilter",
      // Cells hold ISO strings, not Date objects, so the date filter needs to be
      // told how to compare them.
      filterParams: {
        comparator: (filterDate: Date, cellValue: any) => {
          if (isBlank(cellValue)) return -1;
          const cell = moment(cellValue, moment.ISO_8601, false);
          if (!cell.isValid()) return -1;
          const cellDay = cell.startOf("day").toDate();
          if (cellDay < filterDate) return -1;
          if (cellDay > filterDate) return 1;
          return 0;
        },
      },
      valueFormatter: (params: any) =>
        isBlank(params.value) ? "" : formatDateValue(params.value),
      cellRenderer: (params: any) =>
        isBlank(params.value)
          ? emptyCell()
          : escapeHtml(formatDateValue(params.value)),
    };
  }

  if (typeof sample === "number") {
    return { ...base, minWidth: 120, filter: "agNumberColumnFilter" };
  }

  return {
    ...base,
    cellRenderer: (params: any) =>
      isBlank(params.value) ? emptyCell() : escapeHtml(params.value),
  };
}

function emptyCell(): string {
  return `<span class="text-muted">&mdash;</span>`;
}

/** One entry of the territory multi-select: `territory_dim_key` as the value,
 *  `territory_name` as the label. Keys are kept as strings — the API does not
 *  promise a numeric type, so nothing here casts them. */
export interface DuTerritoryOption {
  key: string;
  name: string;
}

function keyText(value: any): string {
  return value == null ? "" : String(value).trim();
}

/**
 * `get-territory-list`: the brand's territories, already sorted by name. `[]` is
 * a brand with none, which is an empty state rather than a failure.
 *
 * A row with no key is dropped — it could be neither selected nor saved. A row
 * with no name is kept and shown by its key.
 */
export function readTerritoryList(res: any): DuTerritoryOption[] {
  const rows = Array.isArray(res)
    ? res
    : Array.isArray(res?.Table)
    ? res.Table
    : Array.isArray(res?.data)
    ? res.data
    : [];

  return rows
    .map((row: any) => {
      const key = keyText(pick(row, "territory_dim_key"));
      return { key, name: keyText(pick(row, "territory_name")) || key };
    })
    .filter((territory: DuTerritoryOption) => !!territory.key);
}

/**
 * `territory_keys` off `get-user-details` — the user's territories for the
 * `brand_key` that was sent. Always present on a current API, `[]` when none.
 *
 * `null` when the field is missing altogether: an API build from before
 * territories, where "holds none" cannot be told apart from "not reported".
 * The caller must not treat that as `[]`, or the next save would clear them.
 */
export function readUserTerritoryKeys(res: any): string[] | null {
  const keys = pick(res, "territory_keys");
  if (!Array.isArray(keys)) return null;
  return keys.map(keyText).filter((key) => !!key);
}
